import { createFileRoute, Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Badge, Button, Card, EmptyState, Input, Spinner } from '@/components';
import type { StorybookListItem } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';

function StorybooksList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'hmonglus' | 'moob'>('hmonglus');
  const [audioVoices, setAudioVoices] = useState<'dual' | 'single'>('dual');
  const [requiresSubscription, setRequiresSubscription] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'storybooks'],
    queryFn: () => apiJson<StorybookListItem[]>('/v1/admin/storybooks'),
  });

  const createBook = useMutation({
    mutationFn: (payload: {
      name: string;
      mode: string;
      audio_voices: string;
      requires_subscription: boolean;
    }) =>
      apiJson<{ id: number }>('/v1/admin/storybooks', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (created) => {
      setName('');
      setRequiresSubscription(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'storybooks'] });
      void navigate({
        to: '/admin/storybooks/$id',
        params: { id: String(created.id) },
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Storybooks</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create cloud storybooks (draft → live). Upload page images and audio, then publish
          without an app release.
        </p>
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">New storybook</h2>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Book name (e.g. Zoo Animals)"
            className="max-w-xs"
          />
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={mode}
            onChange={(e) => {
              const next = e.target.value as 'hmonglus' | 'moob';
              setMode(next);
              setAudioVoices(next === 'moob' ? 'single' : 'dual');
            }}
          >
            <option value="hmonglus">Hmonglus</option>
            <option value="moob">Moob</option>
          </select>
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={audioVoices}
            onChange={(e) => setAudioVoices(e.target.value as 'dual' | 'single')}
          >
            <option value="dual">Dual voice (adult + child)</option>
            <option value="single">Single voice</option>
          </select>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresSubscription}
              onChange={(e) => setRequiresSubscription(e.target.checked)}
            />
            Subscribers only
          </label>
          <Button
            disabled={!name.trim() || createBook.isPending}
            onClick={() =>
              createBook.mutate({
                name: name.trim(),
                mode,
                audio_voices: audioVoices,
                requires_subscription: requiresSubscription,
              })
            }
          >
            {createBook.isPending ? 'Creating…' : 'Create storybook'}
          </Button>
        </div>
        {createBook.isError ? (
          <p className="text-sm text-red-600">{(createBook.error as Error).message}</p>
        ) : null}
      </Card>

      {!data || data.length === 0 ? (
        <EmptyState title="No cloud storybooks yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((book) => (
            <Link
              key={book.id}
              to="/admin/storybooks/$id"
              params={{ id: String(book.id) }}
              className={clsx(
                'group block rounded-[22px] focus:outline-none',
                'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              )}
            >
              <Card
                className={clsx(
                  'h-full transition',
                  'group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-medium text-gray-900">{book.name}</h2>
                  <div className="flex shrink-0 flex-wrap justify-end gap-1">
                    <Badge variant={book.status === 'live' ? 'success' : 'warning'}>
                      {book.status}
                    </Badge>
                    {book.requires_subscription ? (
                      <Badge variant="warning">Premium</Badge>
                    ) : null}
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {book.mode} · {book.audio_voices} · {book.page_count} page
                  {book.page_count === 1 ? '' : 's'} · v{book.content_version}
                </p>
                <p className="mt-2 font-mono text-xs text-gray-400">{book.slug}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StorybooksPage() {
  const matches = useMatches();
  const isDetail = matches.some((m) => m.routeId === '/admin/storybooks/$id');
  if (isDetail) return <Outlet />;
  return <StorybooksList />;
}

export const Route = createFileRoute('/admin/storybooks')({
  component: StorybooksPage,
});
